/**
 * AI推定設置高さ（参考）→ 施工情報（脚立の目安・必要人数・施工難易度）の導出。
 *
 * 由来：中島社長メモ（2026-07）。
 *  - ルーム本体の高さ ≈ 550mm、天カセ対角 ≈ 1,500mm を基準に、
 *    室内機から床までを「何個分」でAIが概算 → 設置高さ(m)を推定。
 *  - 主役は「床からエアコンまでの高さ」。脚立はあくまで“目安”。
 *
 * 脚立の目安（濱田/中島の指摘を反映・2026-07-12）：
 *  作業者が脚立上で腕を伸ばすと、脚立の高さ＋約1.6mまで届く（例: 3尺でも実質+160cm）。
 *  したがって必要な脚立高 ≈ 設置高さ − 1.6m。等号ではない（以前の「脚立≒設置高さ」は過大だった）。
 *
 * すべて「参考／目安」。価格・工事費は一切算出しない（AI金額算出禁止の禁則を順守）。
 */

/** 1尺 = 303mm */
const SHAKU_MM = 303;
/** 脚立上で腕を伸ばした際に届く高さの上乗せ（m）。中島社長メモ: 3尺でも+160cm。 */
const REACH_M = 1.6;
/** 市販脚立の代表サイズ（尺） */
const STANDARD_SHAKU = [3, 4, 5, 6, 7, 8, 10, 12] as const;

export interface InstallInfo {
  /** 推定設置高さ（m・小数1桁） */
  heightM: number;
  /** 例: "約2.8m" */
  heightLabel: string;
  /** 脚立の目安（尺）。0=ほぼ不要。 */
  ladderShaku: number;
  /** 例: "約4尺" / "脚立ほぼ不要" */
  ladderLabel: string;
  /** 補足（例: "腕+約1.6m考慮"）。 */
  ladderNote: string;
  /** 例: "2名推奨" / "1名" */
  crewLabel: string;
  /** 施工難易度 1〜5 */
  difficulty: number;
  /** 例: "★★★☆☆" */
  difficultyStars: string;
}

/** 推定設置高さ（m）から施工情報を導く。heightM が無効なら null。 */
export function deriveInstallInfo(heightM: number | null | undefined): InstallInfo | null {
  if (heightM == null || !Number.isFinite(heightM) || heightM <= 0) return null;
  const h = Math.round(heightM * 10) / 10;

  // 脚立の目安：作業高＝脚立高＋約1.6m（腕の届く分）。必要脚立高 ≈ 設置高さ − 1.6m。
  const needMeter = Math.max(0, h - REACH_M);
  let ladderShaku = 0;
  let ladderLabel = "脚立ほぼ不要";
  let ladderNote = "腕の届く範囲内";
  if (needMeter >= 0.6) {
    const neededShaku = (needMeter * 1000) / SHAKU_MM;
    ladderShaku = 3;
    let best = Infinity;
    for (const s of STANDARD_SHAKU) {
      const d = Math.abs(s - neededShaku);
      if (d < best) { best = d; ladderShaku = s; }
    }
    ladderLabel = `約${ladderShaku}尺`;
    ladderNote = "腕+約1.6m考慮";
  }

  // 必要人数：高所は2名推奨（墜落・接触事故の防止）
  const crewLabel = h < 2.5 ? "1名" : "2名推奨";

  // 施工難易度（1〜5）：高さで段階化（例）3.4m → ★★★☆☆）
  const difficulty = h < 2.5 ? 2 : h < 3.5 ? 3 : h < 4.5 ? 4 : 5;

  return {
    heightM: h,
    heightLabel: `約${h.toFixed(1)}m`,
    ladderShaku,
    ladderLabel,
    ladderNote,
    crewLabel,
    difficulty,
    difficultyStars: "★".repeat(difficulty) + "☆".repeat(5 - difficulty),
  };
}
